import React, { useId } from 'react';

/**
 * Form controls. Every one of these renders a *visible* label — placeholder-only
 * labels vanish the moment someone types and are a documented accessibility
 * failure. Errors render next to the field they belong to, not in a summary
 * block at the top of the form.
 *
 * `text-base` (16px) on mobile is load-bearing: iOS Safari zooms the viewport
 * when a focused input is smaller than that.
 */

const controlClasses = (hasError) =>
  [
    'block w-full min-h-11 rounded-md border bg-surface px-3 py-2',
    'text-base sm:text-sm text-foreground',
    'transition-colors duration-fast ease-out',
    'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-fg',
    hasError
      ? 'border-danger focus:border-danger'
      : 'border-border-strong hover:border-muted-fg focus:border-accent',
  ].join(' ');

export const FieldWrapper = ({ id, label, required, error, hint, className = '', children }) => (
  <div className={`w-full ${className}`}>
    {label && (
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-foreground">
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden="true">
            *
          </span>
        )}
      </label>
    )}
    {children}
    {hint && !error && <p className="mt-1.5 text-xs text-muted-fg">{hint}</p>}
    {error && (
      <p className="mt-1.5 flex items-start gap-1 text-xs font-medium text-danger" role="alert">
        <svg className="mt-0.5 h-3.5 w-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 012 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z"
            clipRule="evenodd"
          />
        </svg>
        {error}
      </p>
    )}
  </div>
);

export const Input = React.forwardRef(function Input(
  { label, error, hint, required, className, id: providedId, ...rest },
  ref
) {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <FieldWrapper id={id} label={label} required={required} error={error} hint={hint} className={className}>
      <input
        ref={ref}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={controlClasses(Boolean(error))}
        {...rest}
      />
    </FieldWrapper>
  );
});

export const Textarea = React.forwardRef(function Textarea(
  { label, error, hint, required, className, rows = 4, id: providedId, ...rest },
  ref
) {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <FieldWrapper id={id} label={label} required={required} error={error} hint={hint} className={className}>
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        required={required}
        aria-invalid={error ? true : undefined}
        className={`${controlClasses(Boolean(error))} resize-y leading-relaxed`}
        {...rest}
      />
    </FieldWrapper>
  );
});

export const Select = React.forwardRef(function Select(
  { label, error, hint, required, className, children, id: providedId, ...rest },
  ref
) {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <FieldWrapper id={id} label={label} required={required} error={error} hint={hint} className={className}>
      <div className="relative">
        <select
          ref={ref}
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          className={`${controlClasses(Boolean(error))} cursor-pointer appearance-none pr-9`}
          {...rest}
        >
          {children}
        </select>
        <svg
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-fg"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </FieldWrapper>
  );
});

export const Checkbox = React.forwardRef(function Checkbox(
  { label, description, className = '', id: providedId, ...rest },
  ref
) {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    // The whole row is the hit target, which gets a checkbox to 44px tall.
    <label
      htmlFor={id}
      className={`flex min-h-11 cursor-pointer items-start gap-3 rounded-md py-2 ${className}`}
    >
      <input
        ref={ref}
        id={id}
        type="checkbox"
        className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-border-strong"
        {...rest}
      />
      <span className="min-w-0">
        <span className="block text-sm text-foreground">{label}</span>
        {description && <span className="block text-xs text-muted-fg">{description}</span>}
      </span>
    </label>
  );
});

/** Groups related fields under a heading so long forms stay scannable. */
export const FieldSet = ({ legend, description, className = '', children }) => (
  <fieldset className={`min-w-0 ${className}`}>
    <legend className="mb-1 text-sm font-semibold uppercase tracking-wide text-muted-fg">
      {legend}
    </legend>
    {description && <p className="mb-4 text-sm text-muted-fg">{description}</p>}
    <div className={description ? '' : 'mt-4'}>{children}</div>
  </fieldset>
);
